import { CameraTargetProps, Number3 } from '@fabric-web/3d-viewer';
import { updateUrl } from '@/utils';

export const INFINITY_SCALE = 1e12;

export const ALL_HIDDEN = 'all-hidden';

export const encodeNumber3 = (value: Number3, transform?: (n: number) => number): string[] =>
  value.map((n) => (transform ? transform(n) : n).toString());

export const parseNumber3 = (
  raw: string | (string | null)[] | null | undefined,
  transform?: (n: number) => number
): Number3 | undefined => {
  if (raw === undefined || raw === null) return undefined;
  const tokens = Array.isArray(raw) ? raw : raw.split(',');
  const parts = tokens.map((part) => (part === null ? Number.NaN : Number.parseFloat(part)));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return undefined;
  return (transform ? parts.map(transform) : parts) as Number3;
};

export const encodeVisibleIndexes = <T extends { isVisible?: boolean }>(
  byId: { [id: string]: T } | undefined
): string | string[] | undefined => {
  if (!byId) return undefined;
  const entries = Object.entries(byId);
  if (entries.length === 0) return undefined;
  const visibleIndexes = entries
    .map(([, value], index) => (value.isVisible === true ? index : -1))
    .filter((index) => index >= 0);
  if (visibleIndexes.length === entries.length) return undefined;
  if (visibleIndexes.length === 0) return ALL_HIDDEN;
  return visibleIndexes.map((index) => index.toString());
};

export const applyVisibilityByIndex = <T extends { isVisible?: boolean }>(
  current: { [id: string]: T } | undefined,
  raw: string | (string | null)[] | null | undefined
): { [id: string]: T } | undefined => {
  if (!current || raw === undefined || raw === null) return current;
  const tokens = Array.isArray(raw) ? raw.filter((t): t is string => !!t) : [raw];
  const allHidden = tokens.length === 1 && tokens[0] === ALL_HIDDEN;
  const indexes = allHidden
    ? new Set<number>()
    : new Set(tokens.map((token) => Number.parseInt(token, 10)).filter((n) => !Number.isNaN(n)));
  return Object.fromEntries(
    Object.entries(current).map(([id, value], index) => [
      id,
      { ...value, isVisible: indexes.has(index) },
    ])
  );
};

export const parseFloatParameter = (queryParam: string | (string | null)[]): number => {
  if (typeof queryParam === 'string') {
    return Number.parseFloat(queryParam);
  } else {
    return 0;
  }
};

export const updateCameraTargetUrlParameters = (cameraTarget: CameraTargetProps) => {
  const [lx, ly, lz] = cameraTarget.lookAt;
  updateUrl('look.x', lx.toString());
  updateUrl('look.y', ly.toString());
  updateUrl('look.z', lz.toString());
  const [px, py, pz] = cameraTarget.position ?? [0, 0, 0];
  updateUrl('position.x', px.toString());
  updateUrl('position.y', py.toString());
  updateUrl('position.z', pz.toString());
  updateUrl('fov', cameraTarget.fov ? cameraTarget.fov.toString() : '0');
};

export const sanitiseScale = (n: number) => (Number.isFinite(n) ? n : INFINITY_SCALE);
export const restoreScale = (n: number) => (n >= INFINITY_SCALE ? Number.POSITIVE_INFINITY : n);
